import { useCallback } from 'react';

import {
  activeSidebarChatIdAtom,
  sidebarChatIdAtomFamily,
} from '@/atoms/chatSidebarAtoms';
import {
  useSetEvidenceChatOpen,
  useSetWorkComposerMode,
} from '@/components/ui/session-ask-heidi/use-persisted-work-mode';
import { useQueryChatById } from '@/hooks/query/useQueryChatById';
import { useQuerySessionChats } from '@/hooks/query/useQuerySessionChats';
import { useSelectWorkPanelProject } from '@heidi/agents/data';
import { ChatListItemVersion } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';

interface SessionWorkPanelEvidenceChromeOptions {
  sessionId: string;
  /**
   * Set when the panel shows the classic Evidence session chat (Evidence only):
   * the chat's id, or null for a new one. `undefined` means Work owns the panel.
   */
  evidenceChatId: string | null | undefined;
  /** Work's own project pick; used as-is while Work owns the panel. */
  onSelectProject?: (projectId: string) => void;
}

/**
 * The Evidence-chat side of `SessionWorkPanelHeader`'s chrome: which chat the
 * header drives while Evidence only holds the panel, the session's Evidence
 * chats for the selector, and the three hand-offs (open an Evidence chat, start
 * a new one from Work home, hand the panel back to a Work project).
 *
 * Lives beside the landing view-model rather than in the header because that
 * `.tsx` sits at the line-count lint budget and `src/features/projects/` is
 * locked to new modules; the behaviour is unchanged.
 */
export const useSessionWorkPanelEvidenceChrome = ({
  sessionId,
  evidenceChatId,
  onSelectProject,
}: SessionWorkPanelEvidenceChromeOptions) => {
  const isEvidence = evidenceChatId !== undefined;
  const setWorkComposerMode = useSetWorkComposerMode();
  const setEvidenceChatOpen = useSetEvidenceChatOpen();
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);
  const setSidebarChatId = useSetAtom(sidebarChatIdAtomFamily(sessionId));
  const selectWorkPanelProject = useSelectWorkPanelProject(sessionId);
  const { data: evidenceChat } = useQueryChatById(isEvidence ? evidenceChatId : null);
  const { data: sessionChats } = useQuerySessionChats(sessionId);
  const evidenceChats = (sessionChats?.chats ?? []).filter(
    (chat) => chat.version === ChatListItemVersion.Value1
  );

  const openEvidenceChat = useCallback(
    (chatId: string | null) => {
      setActiveSidebarChatId(null);
      setSidebarChatId(chatId);
      setWorkComposerMode('evidence');
    },
    [setActiveSidebarChatId, setSidebarChatId, setWorkComposerMode]
  );

  // A new chat always starts from Work home; Evidence only stays a chip there.
  const startNewEvidenceChat = useCallback(() => {
    setActiveSidebarChatId(null);
    setSidebarChatId(null);
    setEvidenceChatOpen(null);
  }, [setActiveSidebarChatId, setSidebarChatId, setEvidenceChatOpen]);

  const handleSelectProject = useCallback(
    (nextProjectId: string) => {
      if (!isEvidence) {
        onSelectProject?.(nextProjectId);
        return;
      }
      setActiveSidebarChatId(null);
      setSidebarChatId(null);
      selectWorkPanelProject(nextProjectId);
    },
    [
      isEvidence,
      onSelectProject,
      selectWorkPanelProject,
      setActiveSidebarChatId,
      setSidebarChatId,
    ]
  );

  return {
    isEvidence,
    evidenceChatTitle: evidenceChat?.title,
    evidenceChats,
    openEvidenceChat,
    startNewEvidenceChat,
    handleSelectProject,
  };
};
